import React from 'react';
import { Document, Image, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { formattaData } from '../utils/dateUtils';

const IVA_ALIQUOTA = 0.081;

const DATI_AZIENDA = {
  nome: 'C.R.G. Scavi e trasporti Sagl',
  indirizzo: 'Via Livio 5, 6830 Chiasso TI',
  contatti: 'Tel. +41 78 751 32 51 - email: info@crgscavi.ch',
  iva: 'IVA: CHE-285.549.695',
};

const pdfStyles = StyleSheet.create({
  page: { padding: 30, fontSize: 10, fontFamily: 'Helvetica' },
  intestazione: { flexDirection: 'row', alignItems: 'center', marginBottom: 16, paddingBottom: 10, borderBottom: '1 solid #ccc' },
  logoImg: { width: 80, height: 32, objectFit: 'contain' },
  recapiti: { marginLeft: 12, fontSize: 9, color: '#333' },
  recapitiNome: { fontSize: 10, fontWeight: 'bold', marginBottom: 2 },
  intestazioneRigheContainer: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 44 },
  blocSinistra: { width: '45%' },
  blocDestra: { width: '45%' },
  label: { fontWeight: 'bold' },
  titolo: { fontSize: 16, fontWeight: 'bold', textAlign: 'center', marginBottom: 16 },
  cantiereRiga: { backgroundColor: '#eee', padding: 6, marginBottom: 8, fontWeight: 'bold' },
  tableHeader: { flexDirection: 'row', backgroundColor: '#1a1a1a', padding: 5, color: '#fff', fontWeight: 'bold' },
  tableRow: { flexDirection: 'row', padding: 5, borderBottom: '1 solid #ccc' },
  colDescr: { width: '44%' },
  colUnita: { width: '10%' },
  colQta: { width: '12%' },
  colPrezzo: { width: '16%' },
  colTotale: { width: '18%', textAlign: 'right' },
  totaliBox: { marginTop: 16, alignSelf: 'flex-end', width: '45%' },
  totaliRiga: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 },
  totaliRigaFinale: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4, borderTop: '1 solid #333', marginTop: 4, fontWeight: 'bold', fontSize: 12 },
});

function fmtCHF(numero) {
  const n = Number(numero) || 0;
  return n.toLocaleString('it-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PreventivoPDF({ preventivo, cliente, cantiere }) {
  const scontoPercento = Number(preventivo.scontoPercento) || 0;
  const subtotale = Number(preventivo.subtotale) || 0;
  const imponibile = Number(preventivo.imponibile) || 0;
  const iva = preventivo.iva != null
    ? Number(preventivo.iva)
    : imponibile * IVA_ALIQUOTA;
  const totale = Number(preventivo.totale) || 0;

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.intestazione}>
          <Image src="/CRG_logo.png" style={pdfStyles.logoImg} />
          <View style={pdfStyles.recapiti}>
            <Text style={pdfStyles.recapitiNome}>{DATI_AZIENDA.nome}</Text>
            <Text>{DATI_AZIENDA.indirizzo}</Text>
            <Text>{DATI_AZIENDA.contatti}</Text>
            <Text>{DATI_AZIENDA.iva}</Text>
          </View>
        </View>

        <Text style={pdfStyles.titolo}>PREVENTIVO</Text>

        <View style={pdfStyles.intestazioneRigheContainer}>
          <View style={pdfStyles.blocSinistra}>
            <Text><Text style={pdfStyles.label}>Chiasso, </Text>{formattaData(preventivo.dataPreventivo)}</Text>
            <Text><Text style={pdfStyles.label}>Preventivo N°: </Text>{preventivo.numeroPreventivo || '-'}</Text>
          </View>

          <View style={pdfStyles.blocDestra}>
            <Text style={pdfStyles.label}>Spett.le</Text>
            <Text style={{ marginBottom: 12 }}>{cliente?.ragioneSociale || ''}</Text>
            {cliente?.indirizzo ? <Text>{cliente.indirizzo}</Text> : null}
            {(cliente?.cap || cliente?.citta) ? (
              <Text>{[cliente.cap, cliente.citta].filter(Boolean).join(' ')}</Text>
            ) : null}
          </View>
        </View>

        {cantiere ? (
          <Text style={pdfStyles.cantiereRiga}>Cantiere: {cantiere.luogo}</Text>
        ) : null}

        <View style={pdfStyles.tableHeader}>
          <Text style={pdfStyles.colDescr}>Descrizione</Text>
          <Text style={pdfStyles.colUnita}>U</Text>
          <Text style={pdfStyles.colQta}>Q.tà</Text>
          <Text style={pdfStyles.colPrezzo}>Prezzo CHF</Text>
          <Text style={pdfStyles.colTotale}>Totale CHF</Text>
        </View>

        {(preventivo.righe || []).map((riga, indice) => (
          <View key={indice} style={pdfStyles.tableRow}>
            <Text style={pdfStyles.colDescr}>{riga.descrizione || ''}</Text>
            <Text style={pdfStyles.colUnita}>{riga.unita || ''}</Text>
            <Text style={pdfStyles.colQta}>{riga.quantita || ''}</Text>
            <Text style={pdfStyles.colPrezzo}>{fmtCHF(riga.prezzo)}</Text>
            <Text style={pdfStyles.colTotale}>{fmtCHF(riga.totale)}</Text>
          </View>
        ))}

        <View style={pdfStyles.totaliBox}>
          <View style={pdfStyles.totaliRiga}>
            <Text>Subtotale</Text>
            <Text>CHF {fmtCHF(subtotale)}</Text>
          </View>

          {scontoPercento > 0 && (
            <View style={pdfStyles.totaliRiga}>
              <Text>Sconto {scontoPercento}%</Text>
              <Text>- CHF {fmtCHF(subtotale - imponibile)}</Text>
            </View>
          )}

          <View style={pdfStyles.totaliRiga}>
            <Text>Imponibile</Text>
            <Text>CHF {fmtCHF(imponibile)}</Text>
          </View>

          <View style={pdfStyles.totaliRiga}>
            <Text>IVA 8.1%</Text>
            <Text>CHF {fmtCHF(iva)}</Text>
          </View>

          <View style={pdfStyles.totaliRigaFinale}>
            <Text>TOTALE</Text>
            <Text>CHF {fmtCHF(totale)}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}